import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'

import { discussionCsApi } from './chapterDiscussions.api'
import {
  buildReportQuery,
  discussionCommentIdentityKey,
} from './chapterDiscussions.model'

import type {
  CommentReportReason,
  DiscussionReportListItem,
  ListDiscussionReportsQuery,
} from './chapterDiscussions.types'

export interface DiscussionReportListProps {
  initialQuery?: ListDiscussionReportsQuery
}

type FilterType =
  | 'all'
  | 'status'
  | 'reason'
  | 'book'
  | 'reporter'
  | 'author'
  | 'exact'

export function DiscussionReportList({
  initialQuery = {},
}: DiscussionReportListProps) {
  const navigate = useNavigate()
  const requestVersion = useRef(0)
  const [query, setQuery] = useState<ListDiscussionReportsQuery>(() =>
    buildReportQuery({ ...initialQuery, limit: initialQuery.limit ?? 50 }),
  )
  const [items, setItems] = useState<DiscussionReportListItem[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filterType, setFilterType] = useState<FilterType>('all')
  const [status, setStatus] =
    useState<ListDiscussionReportsQuery['status']>('open')
  const [reason, setReason] = useState<CommentReportReason>('policy_violation')
  const [value, setValue] = useState('')
  const [chapterId, setChapterId] = useState('')

  const load = useCallback(
    async (cursor?: string, append = false) => {
      const version = ++requestVersion.current
      setLoading(true)
      setError(null)
      try {
        const response = await discussionCsApi.listReports({
          ...query,
          cursor,
        })
        if (version !== requestVersion.current) return
        setItems(current =>
          append ? [...current, ...response.items] : response.items,
        )
        setNextCursor(response.nextCursor)
      } catch (requestError) {
        if (version === requestVersion.current) {
          setError(
            requestError instanceof Error
              ? requestError.message
              : 'Unable to load reports',
          )
        }
      } finally {
        if (version === requestVersion.current) setLoading(false)
      }
    },
    [query],
  )

  useEffect(() => {
    load()
  }, [load])

  const applyFilter = (event: FormEvent) => {
    event.preventDefault()
    try {
      const base = { limit: 50 }
      const next =
        filterType === 'all'
          ? base
          : filterType === 'status'
            ? { ...base, status }
            : filterType === 'reason'
              ? { ...base, reason }
              : filterType === 'book'
                ? {
                    ...base,
                    bookId: Number(value),
                    chapterId: chapterId ? Number(chapterId) : undefined,
                  }
                : filterType === 'reporter'
                  ? { ...base, reporterUserId: Number(value) }
                  : filterType === 'author'
                    ? { ...base, reportedAuthorId: Number(value) }
                    : { ...base, exactCommentId: value.trim() }
      setQuery(buildReportQuery(next))
    } catch (filterError) {
      setError(
        filterError instanceof Error ? filterError.message : 'Invalid filter',
      )
    }
  }

  return (
    <Box>
      <Paper component="form" onSubmit={applyFilter} sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          useFlexGap
          flexWrap="wrap"
          alignItems={{ xs: 'stretch', md: 'flex-end' }}>
          <TextField
            select
            label="Filter"
            value={filterType}
            sx={{ width: { xs: '100%', md: 210 } }}
            onChange={event => setFilterType(event.target.value as FilterType)}>
            <MenuItem value="all">All reports</MenuItem>
            <MenuItem value="status">Report status</MenuItem>
            <MenuItem value="reason">Report reason</MenuItem>
            <MenuItem value="book">Book / chapter</MenuItem>
            <MenuItem value="reporter">Reporter ID</MenuItem>
            <MenuItem value="author">Reported author ID</MenuItem>
            <MenuItem value="exact">Exact comment ID</MenuItem>
          </TextField>
          {filterType === 'status' && (
            <TextField
              select
              label="Status"
              value={status}
              sx={{ width: { xs: '100%', md: 180 } }}
              onChange={event =>
                setStatus(
                  event.target.value as ListDiscussionReportsQuery['status'],
                )
              }>
              {['open', 'resolved', 'dismissed'].map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          )}
          {filterType === 'reason' && (
            <TextField
              select
              label="Reason"
              value={reason}
              sx={{ width: { xs: '100%', md: 220 } }}
              onChange={event =>
                setReason(event.target.value as CommentReportReason)
              }>
              {[
                'policy_violation',
                'harassment',
                'spam_or_irrelevant',
                'personal_information',
                'other',
              ].map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          )}
          {!['all', 'status', 'reason'].includes(filterType) && (
            <TextField
              required
              label={
                filterType === 'book'
                  ? 'Book ID'
                  : filterType === 'reporter'
                    ? 'Reporter ID'
                    : filterType === 'author'
                      ? 'Author ID'
                      : 'Comment ID'
              }
              type={filterType === 'exact' ? 'text' : 'number'}
              value={value}
              sx={{ width: { xs: '100%', md: 220 } }}
              onChange={event => setValue(event.target.value)}
            />
          )}
          {filterType === 'book' && (
            <TextField
              label="Chapter ID (optional)"
              type="number"
              value={chapterId}
              sx={{ width: { xs: '100%', md: 220 } }}
              onChange={event => setChapterId(event.target.value)}
            />
          )}
          <Button
            type="submit"
            variant="contained"
            size="small"
            sx={{
              alignSelf: 'flex-end',
              ml: 'auto',
              mb: { xs: 0, md: 0.5 },
              minWidth: 112,
            }}>
            Apply filter
          </Button>
        </Stack>
      </Paper>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Report</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Reporter</TableCell>
              <TableCell>Author</TableCell>
              <TableCell>Book / chapter</TableCell>
              <TableCell>Evidence</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(item => (
              <TableRow
                hover
                key={`${item.comment.commentId}:${item.report.reportId}`}
                sx={{ cursor: 'pointer' }}
                onClick={() =>
                  navigate(
                    `/chapter-discussions/${discussionCommentIdentityKey(
                      item.comment,
                    )}/show`,
                  )
                }>
                <TableCell>{item.report.reportId}</TableCell>
                <TableCell>
                  <Chip size="small" label={item.report.status} />
                </TableCell>
                <TableCell>{item.report.reason}</TableCell>
                <TableCell>{item.report.reporterUserId}</TableCell>
                <TableCell>{item.comment.authorId}</TableCell>
                <TableCell>
                  {item.comment.bookId} / {item.comment.chapterId}
                </TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Typography variant="body2" noWrap>
                    {item.report.reportedBodySnapshot ?? 'Evidence unavailable'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No reports match this filter.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack alignItems="center" sx={{ mt: 2 }}>
        {loading ? (
          <CircularProgress size={28} />
        ) : (
          nextCursor && (
            <Button onClick={() => load(nextCursor, true)}>Load more</Button>
          )
        )}
      </Stack>
    </Box>
  )
}
